import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonSpinner
} from '@ionic/react';
import { RootState } from 'core/store';
import { ONE_SECOND } from 'core/constants';
import { ProductModelSearchResult } from 'core/types';
import { searchProductModels } from 'features/shared/inventory/store/InventoryActions';
import { resetProductModels } from 'features/shared/inventory/store/InventorySlice';
import { searchOutline } from 'ionicons/icons';
import ArrowIcon from 'assets/icons/arrow-down.svg';
import './ProductModelSearchSelect.scss';

interface ProductModelSearchSelectProps {
  /** Currently-selected `product_template_id`, or null/undefined if none picked yet. */
  value?: number | null;
  /**
   * Label to render for `value` when it isn't present in the latest search
   * results (e.g. editing an existing product before the user has typed).
   */
  valueLabel?: string | null;
  placeholder?: string;
  disabled?: boolean;
  /** Scope results to an already-chosen Product Type (design.md D7). */
  typeId?: number | null;
  /** Scope results to an already-chosen Brand/Manufacturer (design.md D7). */
  manufacturerId?: number | null;
  /** Appended alongside the root class — e.g. to set width/centering per usage. */
  className?: string;
  /**
   * `compact` (default): the inline cascading-edit-form look — closed field
   * shows a trailing chevron, 32px tall. `search`: the prominent Figma "Add
   * an Item" look — a leading search icon, no chevron, 48px tall.
   */
  variant?: 'compact' | 'search';
  onChange: (result: ProductModelSearchResult) => void;
}

/**
 * App-native (Ionic) "Product Model" search-select. Queries the homeowner
 * product-model catalog search (`searchProductModels`), debounced ~1s and
 * skipped until the user types, mirroring the admin `Select showSearch` UX:
 * options render as `{ value: template id, label: name }`, and the currently
 * selected label stays visible even when it isn't in the latest results.
 * When `typeId`/`manufacturerId` are provided (cascading edit flow), results
 * are scoped to that Type/Brand instead of the unscoped catalog.
 */
const ProductModelSearchSelect: React.FC<ProductModelSearchSelectProps> = ({
  value,
  valueLabel,
  placeholder,
  disabled,
  typeId,
  manufacturerId,
  className,
  variant = 'compact',
  onChange
}) => {
  const isSearchVariant = variant === 'search';
  const dispatch = useDispatch();
  const homeId = useSelector((state: RootState) => state.home.currentHome?.id);
  const { productModels, productModelsLoading } = useSelector(
    (state: RootState) => state.inventory
  );
  const [term, setTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const fieldRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const resultsRef = useRef<HTMLIonListElement>(null);
  const inputRef = useRef<HTMLIonInputElement>(null);
  const [resultsRect, setResultsRect] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  // The IonInput's `autoFocus` HTML attribute only auto-applies to elements
  // present at initial page parse — it's unreliable for this input, which
  // only mounts once the field is clicked (isOpen flips true).
  //
  // Two separate race conditions to dodge here, not one:
  // 1. Stencil inserts the custom element before its own internal render
  //    (the actual native <input> `.setFocus()` needs to find) has finished
  //    — `componentOnReady()` resolves once that's done.
  // 2. The browser may not have upgraded the just-inserted custom element
  //    (attached its class instance/methods) yet at the exact moment this
  //    effect runs, immediately after React's commit — `componentOnReady`
  //    can be `undefined` on the raw, not-yet-upgraded element. `?.()`
  //    optional-call on that silently no-ops with no error, which is worse
  //    than case 1: `.setFocus()` never gets called at all. `rAF` pushes
  //    this past the current paint, by which point the upgrade has always
  //    happened, and `setFocus()` is called unconditionally as a fallback
  //    for the (should-be-impossible-by-then) case componentOnReady is
  //    still missing.
  useEffect(() => {
    if (!isOpen) return;
    const raf = requestAnimationFrame(() => {
      const el = inputRef.current;
      if (!el) return;
      if (typeof el.componentOnReady === 'function') {
        el.componentOnReady().then(() => el.setFocus());
      } else {
        el.setFocus();
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [isOpen]);

  // The results list is portaled to document.body (see render below) so it
  // can overflow outside ancestor containers that clip it (e.g. the
  // Product Details accordion, which needs overflow:hidden for its own
  // expand/collapse animation) — track the field's position so the portaled
  // list can still be pinned directly under it.
  //
  // A plain `window`/`document` scroll listener (even in the capture phase)
  // never fires for this field's actual scroll container: this field always
  // lives inside an `IonContent`, whose scrollable element is inside its
  // Shadow DOM, and native `scroll` events are `composed: false` — they do
  // not cross the shadow boundary at all. Without a listener on the real
  // scroll element, `updateRect` never re-runs and the portaled list stays
  // pinned at its snapshot position while the field scrolls out from under
  // it. Resolve the nearest `ion-content` ancestor's actual scroll element
  // via `getScrollElement()` and listen there instead.
  useLayoutEffect(() => {
    if (!isOpen || !term) {
      setResultsRect(null);
      return;
    }
    const updateRect = () => {
      if (!fieldRef.current) return;
      const rect = fieldRef.current.getBoundingClientRect();
      setResultsRect({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width
      });
    };
    updateRect();
    window.addEventListener('scroll', updateRect, true);
    window.addEventListener('resize', updateRect);

    let scrollEl: HTMLElement | undefined;
    let cancelled = false;
    const ionContent = fieldRef.current?.closest('ion-content') as
      | (HTMLElement & { getScrollElement?: () => Promise<HTMLElement> })
      | null;
    ionContent?.getScrollElement?.().then((el) => {
      if (cancelled) return;
      scrollEl = el;
      scrollEl.addEventListener('scroll', updateRect);
    });

    return () => {
      cancelled = true;
      window.removeEventListener('scroll', updateRect, true);
      window.removeEventListener('resize', updateRect);
      scrollEl?.removeEventListener('scroll', updateRect);
    };
  }, [isOpen, term]);

  const handleSearchChange = (nextTerm: string) => {
    setTerm(nextTerm);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!nextTerm) {
      dispatch(resetProductModels());
      return;
    }
    debounceRef.current = setTimeout(() => {
      if (homeId) {
        dispatch(
          searchProductModels(
            homeId,
            nextTerm,
            typeId || undefined,
            manufacturerId || undefined
          )
        );
      }
    }, ONE_SECOND);
  };

  const closeSearch = () => {
    setIsOpen(false);
    setTerm('');
    dispatch(resetProductModels());
  };

  const selectResult = (result: ProductModelSearchResult) => {
    onChange(result);
    closeSearch();
  };

  // Abandoning the search has to close it explicitly. `selectResult` used to be
  // the only path that flipped `isOpen` back to false, and because the results
  // list is portaled to document.body it is not clipped or dismissed by
  // anything on the way out — clicking elsewhere left it floating over
  // unrelated UI until the term was cleared or the component unmounted.
  //
  // `mousedown` (not `click`) fires before the result item's own onClick, so
  // the guard has to cover the portaled list as well as the field; otherwise
  // picking a result would close the list before the selection registered.
  useEffect(() => {
    if (!isOpen) return;
    const onPointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (
        fieldRef.current?.contains(target) ||
        resultsRef.current?.contains(target)
      ) {
        return;
      }
      closeSearch();
    };
    // Escape is scoped to the field (capture phase + stopPropagation), NOT
    // document: while open, focus is in this field's own IonInput, and several
    // consumers of this select live inside an IonModal. Ionic's overlay
    // Escape-to-dismiss listener is registered on document when that modal
    // opens — i.e. before this one would be — so a document-level handler here
    // would lose the ordering race and Escape would close the whole modal
    // instead of just the dropdown.
    const field = fieldRef.current;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      closeSearch();
      // Deferred: the trigger only regains its tabIndex once the isOpen=false
      // render has committed, and focus() is a no-op on a non-focusable node.
      requestAnimationFrame(() => triggerRef.current?.focus());
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    field?.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
      field?.removeEventListener('keydown', onKeyDown, true);
    };
  }, [isOpen]);

  const displayLabel = term || (!isOpen && (valueLabel || '')) || '';

  return (
    <div
      className={`hk-product-model-search-select${
        isSearchVariant ? ' hk-product-model-search-select-search' : ''
      }${className ? ` ${className}` : ''}`}
      ref={fieldRef}
    >
      {/*
        Button semantics only apply while closed. Once open the field's own
        IonInput is the focus target, and a role="button" wrapper around a text
        input would misreport it to assistive tech. Without this the field was
        mouse-only: nothing inside it was focusable until `isOpen` flipped, and
        only a click flipped it — so a keyboard user could never reach the
        model search at all, which is the entry point of the Add an Item flow.
      */}
      <div
        ref={triggerRef}
        className="hk-product-model-search-select-field"
        role={isOpen ? undefined : 'button'}
        tabIndex={isOpen || disabled ? undefined : 0}
        aria-expanded={isOpen ? undefined : false}
        aria-label={
          isOpen
            ? undefined
            : placeholder || valueLabel || 'Search product models'
        }
        onClick={() => !disabled && setIsOpen(true)}
        onKeyDown={(event) => {
          if (isOpen || disabled) return;
          if (event.key === 'Enter' || event.key === ' ') {
            // Space would otherwise scroll the page instead of opening.
            event.preventDefault();
            setIsOpen(true);
          }
        }}
      >
        {isSearchVariant && (
          <IonIcon
            icon={searchOutline}
            className="hk-product-model-search-select-search-icon"
          />
        )}
        {isOpen ? (
          <IonInput
            ref={inputRef}
            className="hk-product-model-search-select-input"
            fill="outline"
            clearInput
            value={term}
            placeholder={placeholder || 'Search product models...'}
            onIonInput={(e) => handleSearchChange(e.detail.value || '')}
          />
        ) : (
          <div className="hk-product-model-search-select-value">
            <span
              className={
                displayLabel
                  ? 'hk-product-model-search-select-value-text'
                  : 'hk-product-model-search-select-value-placeholder'
              }
            >
              {displayLabel || placeholder || 'Search product models...'}
            </span>
            {!isSearchVariant && <IonIcon icon={ArrowIcon} />}
          </div>
        )}
      </div>
      {isOpen &&
        term &&
        resultsRect &&
        createPortal(
          <IonList
            ref={resultsRef}
            className="hk-product-model-search-select-results"
            lines="full"
            style={{
              top: `${resultsRect.top}px`,
              left: `${resultsRect.left}px`,
              width: `${resultsRect.width}px`
            }}
          >
            {productModelsLoading ? (
              <IonItem lines="none">
                <IonSpinner name="dots" />
              </IonItem>
            ) : productModels.length === 0 ? (
              <IonItem lines="none">
                <IonLabel color="medium">No matching products found</IonLabel>
              </IonItem>
            ) : (
              productModels.map((result) => (
                <IonItem
                  key={`product-model-${result.id}`}
                  button
                  detail={false}
                  className={
                    result.id === value
                      ? 'hk-product-model-search-select-result-selected'
                      : ''
                  }
                  onClick={() => selectResult(result)}
                >
                  <IonLabel>
                    <h3>
                      {result.manufacturer} &middot; {result.model}
                    </h3>
                  </IonLabel>
                </IonItem>
              ))
            )}
          </IonList>,
          document.body
        )}
    </div>
  );
};

export default ProductModelSearchSelect;
